import { useEffect, useRef } from 'react';
import { useWriteContract, useWaitForTransactionReceipt, useBalance, useReadContract } from 'wagmi';
import { contractAddress, usdtAddress, usdcAddress } from '../../config/contract';
import { abiBSC } from '../../abiBSC';
import { parseEther, formatUnits } from 'viem';
import { useTransactionPopup } from '../TransactionPopup';

interface BnbPaymentProps {
  amount: string;
  userAddress?: string;
  referralAddress: string;
  stake: boolean;
  onError: (error: string) => void;
  onSuccess: (hash: string) => void;
  onProcessing: (processing: boolean) => void;
}

export function BnbPayment({
  amount,
  userAddress,
  referralAddress,
  stake,
  onError,
  onSuccess,
  onProcessing,
}: BnbPaymentProps) {
  // Add a ref to track processed transaction hashes
  const processedTxHashesRef = useRef<Set<string>>(new Set());

  // Determine chainId (56 for BSC)
  const chainId = 56;

  // Native BNB balance
  const { data: bnbBalanceData } = useBalance({
    address: userAddress as `0x${string}`,
    chainId,
  });

  // USDT/USDC balances on BSC
  const usdtTokenAddress = usdtAddress.bsc;
  const usdcTokenAddress = usdcAddress.bsc;
  const { data: usdtBalanceData } = useBalance({
    address: userAddress as `0x${string}`,
    token: usdtTokenAddress,
    chainId,
  });
  const { data: usdcBalanceData } = useBalance({
    address: userAddress as `0x${string}`,
    token: usdcTokenAddress,
    chainId,
  });

  const {
    data: hash,
    isPending,
    writeContract,
    error: writeError,
    status: writeStatus
  } = useWriteContract();

  const { isLoading: isConfirming, isSuccess, data: transactionReceipt } = useWaitForTransactionReceipt({
    hash,
    confirmations: 2,
  });

  const { showTransaction } = useTransactionPopup();

  // Debug logging to help trace the transaction flow
  useEffect(() => {
    if (hash) {
      console.log(`BNB transaction hash received: ${hash}, status: ${writeStatus}`);
    }
    if (writeError) {
      console.error('BNB transaction error:', writeError);
    }
  }, [hash, writeError, writeStatus]);

  // Extract token information from logs when we have a transaction receipt
  const extractTokenInfo = (receipt: any) => {
    if (!receipt || !receipt.logs) return null;
    
    try {
      // Find TokensBought event (signature: 0x9c46ba47a94a38b7e008b5a7a4ae121852c03bd4d17b04e99e99e6a7c734ad2b)
      const tokensBoughtEvent = receipt.logs.find((log: any) => 
        log.topics[0] === '0x9c46ba47a94a38b7e008b5a7a4ae121852c03bd4d17b04e99e99e6a7c734ad2b'
      );
      
      if (tokensBoughtEvent) {
        // Decode data - first 32 bytes (64 chars) after 0x are tokens
        const tokenData = tokensBoughtEvent.data.substring(2); // Remove 0x prefix
        const tokens = BigInt('0x' + tokenData.substring(0, 64));
        
        // Format with 18 decimals
        const formattedTokens = formatUnits(tokens, 18);
        console.log('Extracted tokens from event:', formattedTokens);
        return formattedTokens;
      }
      
      return null;
    } catch (error) {
      console.error('Error extracting token info from logs:', error);
      return null;
    }
  };

  const handlePayment = async () => {
    if (!userAddress) {
      onError('Please connect your wallet');
      return;
    }

    // Check if referrer address is valid for EVM (BNB is also an EVM chain)
    if (referralAddress && !referralAddress.startsWith('0x')) {
      // Just log the error, don't show to user
      console.error('Invalid referrer address for BNB payments');
      return;
    }

    const inputAmount = parseFloat(amount);
    if (!amount || isNaN(inputAmount) || inputAmount <= 0) {
      onError('Please enter a valid amount');
      return;
    }

    try {
      const bnbValue = parseEther(amount);

      // Check native BNB balance
      if (bnbBalanceData && bnbValue > bnbBalanceData.value) {
        onError(`Insufficient BNB balance. You have ${bnbBalanceData.formatted} BNB`);
        return;
      }

      // Log the start of the transaction
      console.log('Initiating BNB transaction with args:', [stake, referralAddress], 'value:', bnbValue.toString());
      
      const result = await writeContract({
        address: contractAddress.bsc,
        abi: abiBSC,
        functionName: 'buyWithBNB',
        args: [stake, referralAddress],
        value: bnbValue,
      });
      
      console.log('BNB writeContract result:', result);
      
    } catch (err: any) {
      console.error('BNB Transaction failed:', err);
      onError(err.message || 'Transaction failed');
      showTransaction('error', err.message || 'Transaction failed', 'bsc');
      // Ensure processing state is reset when user denies transaction
      onProcessing(false);
    }
  };

  // Show transaction popup as soon as we have a hash
  useEffect(() => {
    if (hash && isPending) {
      console.log('Showing pending transaction popup for hash:', hash);
      showTransaction('pending', `Purchasing $QGEN with ${amount} BNB...`, 'bsc', hash);
    }
  }, [hash, isPending, amount, showTransaction]);

  // Update processing state
  useEffect(() => {
    onProcessing(isPending || isConfirming);
    
    // If the transaction is no longer pending but we don't have success,
    // and it's not confirming (which means it might have failed without an error)
    if (!isPending && !isSuccess && !isConfirming && writeError) {
      showTransaction('error', writeError.message || 'Transaction failed', 'bsc');
      onProcessing(false);
    }
  }, [isPending, isConfirming, onProcessing, isSuccess, writeError, showTransaction]);

  // Handle success
  useEffect(() => {
    let isActive = true; // Track component mount state
    
    if (isSuccess && hash) {
      // Skip if we've already processed this transaction hash
      if (processedTxHashesRef.current.has(hash)) {
        return;
      }
      
      // Mark this transaction as processed
      processedTxHashesRef.current.add(hash);
      
      console.log('BNB transaction confirmed successfully:', hash);
      console.log('Transaction receipt:', transactionReceipt);
      
      // Extract tokens from receipt logs
      const tokens = transactionReceipt ? extractTokenInfo(transactionReceipt) : null;
      const successMessage = tokens 
        ? `Successfully purchased ${parseFloat(tokens).toLocaleString()} QGEN with ${amount} BNB`
        : `Successfully purchased $QGEN with ${amount} BNB`;
      
      // Only call success callback if component is still mounted
      if (isActive) {
      onSuccess(hash);
        showTransaction('success', successMessage, 'bsc', hash, tokens || undefined);
    }
    }
    
    // Cleanup function
    return () => {
      isActive = false;
    };
  }, [isSuccess, hash, transactionReceipt, onSuccess, amount, showTransaction]);

  return { 
    handlePayment,
    bnbBalance: bnbBalanceData?.formatted,
    usdtBalance: usdtBalanceData?.formatted,
    usdcBalance: usdcBalanceData?.formatted,
  };
}
